import { checkFeatureFlagGate } from "./feature-flags/check-feature-flag";
import { featureFlags } from "./feature-flags/feature-flag-manifest";

import type * as CastleJs from "@castleio/castle-js";

// Castle is browser-only, so load it dynamically and keep every SDK operation
// best-effort. Anti-abuse signals must never block WAC startup or navigation.
// `throttling: false` is required because signup redirects can abort Castle's
// default batched request.

type CastleSdk = {
    configure: typeof CastleJs.configure;
    custom: typeof CastleJs.custom;
    createRequestToken: typeof CastleJs.createRequestToken;
};

let isCastleConfigured = false;
let castleSdkPromise: Promise<CastleSdk | null> | null = null;
let configurePromise: Promise<void> | null = null;

export type CastleCustomEvent = {
    name: string;
    properties?: Record<string, string>;
    // User-provided email; events are dropped when it is missing or invalid.
    email?: string;
};

const NORMALIZED_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EMAIL_LENGTH = 254;

const normalizeCastleEmail = (
    email: string | undefined
): string | undefined => {
    if (!email) {
        return undefined;
    }

    const trimmed = email.trim().toLowerCase();

    if (trimmed.length === 0 || trimmed.length > MAX_EMAIL_LENGTH) {
        return undefined;
    }

    if (!NORMALIZED_EMAIL_PATTERN.test(trimmed)) {
        return undefined;
    }

    return trimmed;
};

export const getCastlePublishableKey = () =>
    process.env["CASTLE_PUBLISHABLE_KEY"] || "";

const loadCastleSdk = (): Promise<CastleSdk | null> => {
    if (typeof window === "undefined") {
        return Promise.resolve(null);
    }

    if (!castleSdkPromise) {
        castleSdkPromise = import("@castleio/castle-js")
            .then(({ configure, custom, createRequestToken }) => ({
                configure,
                custom,
                createRequestToken
            }))
            .catch(() => {
                console.error("Failed to load Castle SDK");
                castleSdkPromise = null;
                return null;
            });
    }

    return castleSdkPromise;
};

const dispatchCastleCustomEvent = (
    Castle: CastleSdk,
    { name, properties, email }: CastleCustomEvent
) => {
    try {
        const normalizedEmail = normalizeCastleEmail(email);

        if (!normalizedEmail) {
            return;
        }

        Promise.resolve(
            Castle.custom({
                name,
                properties,
                user: {
                    id: normalizedEmail,
                    email: normalizedEmail
                }
            })
        ).catch(() => {
            console.error("Failed to send Castle custom event");
        });
    } catch {
        console.error("Failed to send Castle custom event");
    }
};

export const configureCastle = () => {
    if (typeof window === "undefined") {
        return;
    }

    const publishableKey = getCastlePublishableKey();

    if (!publishableKey || configurePromise) {
        return;
    }

    configurePromise = loadCastleSdk()
        .then((Castle) => {
            if (!Castle || isCastleConfigured) {
                return;
            }

            try {
                Castle.configure({ pk: publishableKey, throttling: false });
                isCastleConfigured = true;
            } catch {
                console.error("Failed to configure Castle");
            }
        })
        .catch(() => {
            console.error("Failed to configure Castle");
        });
};

// Maximum time the SDK's createRequestToken() is given to resolve before the
// helper falls back to `undefined`. Castle's request token is purely
// observational; the signup redirect must never be blocked waiting for it.
export const CASTLE_REQUEST_TOKEN_TIMEOUT_MS = 100;

/**
 * Fetches a Castle request token from the lazily-loaded SDK, intended to be
 * forwarded as a `castle_token` query param on the identity interstitial
 * redirect so the receiving service can call Castle's `/v1/risk` API
 * server-side. Returns `undefined` if Castle is gated off, unconfigured, the
 * SDK rejects, returns an empty value, or exceeds `timeoutMs`. Never throws.
 */
export const getCastleRequestToken = async (
    timeoutMs: number = CASTLE_REQUEST_TOKEN_TIMEOUT_MS
): Promise<string | undefined> => {
    if (typeof window === "undefined") {
        return undefined;
    }

    if (!checkFeatureFlagGate(featureFlags.castleIoEvent)) {
        return undefined;
    }

    if (!isCastleConfigured || !castleSdkPromise) {
        return undefined;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
        const timeoutPromise = new Promise<undefined>((resolve) => {
            timeoutId = setTimeout(() => resolve(undefined), timeoutMs);
        });

        const tokenPromise = castleSdkPromise.then(async (Castle) => {
            if (!Castle) {
                return undefined;
            }
            try {
                const token = await Castle.createRequestToken();
                return typeof token === "string" && token.length > 0
                    ? token
                    : undefined;
            } catch {
                return undefined;
            }
        });

        return await Promise.race([tokenPromise, timeoutPromise]);
    } catch {
        return undefined;
    } finally {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    }
};

export const sendCastleCustomEvent = (event: CastleCustomEvent) => {
    if (typeof window === "undefined") {
        return;
    }

    if (!checkFeatureFlagGate(featureFlags.castleIoEvent)) {
        return;
    }

    if (!isCastleConfigured || !castleSdkPromise) {
        return;
    }

    castleSdkPromise
        .then((Castle) => {
            if (Castle) {
                dispatchCastleCustomEvent(Castle, event);
            }
        })
        .catch(() => {
            console.error("Failed to send Castle custom event");
        });
};
